import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import ImageSizeAPI from "../api/ImageSizeAPI";

Vue.use(Vuex);

/**
 * The cart's row definition
 *
 * {
 *     image: image
 *     sizes: [
 *         {
 *             image_size_id: 1,
 *             quantity: 1,
 *             line_price: 2.0
 *         },
 *         {
 *             image_size_id: 2,
 *             quantity: 2,
 *             line_price: 4.0
 *         }
 *     ],
 *     items_price: 6.0
 * }
 *
 * */

/**
 * Gets the unit price of the item by it's size id
 * @param state
 * @param image_size_id
 * @returns {*}
 */
const getUnitPriceBySize = (state, image_size_id) => {
    if (image_size_id == undefined) {
        return parseFloat(state.imageSizes[0].unit_price);
    }
    const size = state.imageSizes.find(elem => elem.id == image_size_id);
    return parseFloat(size.unit_price);
}

/**
 * Return the cart total cost
 * @param cart
 * @returns {number}
 */
const getCartTotal = (state) => {
    var totalPrice = 0;
    for (var i = 0; i < state.cart.length; i++) {
        totalPrice = totalPrice + parseFloat(state.cart[i].items_price);
    }
    return totalPrice;
}

/**
 * Calc the cart row price
 * @param state
 * @param line
 * @returns {number}
 */
const calculateLinePrice = (state, size) => {
    return parseFloat(size.quantity) * getUnitPriceBySize(state, size.image_size_id);
}
/**
 * Calcualte the items price for all sizes added
 * @param row
 * @returns {number}
 */
const calculateItemsPrice = (row) => {
    let items_price = 0;
    for (var i = 0; i < row.sizes.length; i++) {
        items_price += parseFloat(row.sizes[i].line_price);
    }
    return items_price;
}

export const store = new Vuex.Store({
    plugins: [createPersistedState()],
    state: {
        gallery: {},
        cart: [],
        totalPrice: 0.0,
        galleryVisible: true,
        cartListingVisible: false,
        checkoutFormVisible: false,
        orderCompletedVisible: false,
        imageAddedToCart: false,
        _timeout: null,
        imageSizes: [],
        order: {
            id: null,
            client_name: '',
            ship_address_1: '',
            ship_address_2: '',
            ship_address_3: '',
            zip_code: '',
            locality: '',
            city: '',
            ship_same_address: true,
            total: 0.0,
            payment_method_id: null,
            phone: '',
            email: '',
        }
    },
    mutations: {
        setGallery: (state, gallery) => {
          state.gallery = gallery;
        },
        addToCart: (state, item) => {
            // find item in cart
            const idx = state.cart.findIndex(function (elem) {
                return elem.image.id == item.image.id;
            });

            // image has not been added yet
            if (idx == -1) {
                const line_price = getUnitPriceBySize(state, item.gallery.available_sizes[0].id);
                const row = {
                    image: item.image,
                    sizes: [
                        {
                            image_size_id: item.gallery.available_sizes[0].id,
                            quantity: 1,
                            line_price: line_price,
                            gallery_id: item.gallery.id
                        }
                    ],
                    items_price: line_price
                };
                // add to cart array
                state.cart.push(row);
            }

            state.totalPrice = getCartTotal(state);
            clearTimeout(state._timeout);
            state.imageAddedToCart = true;
            state._timeout = setTimeout(() => {
                state.imageAddedToCart = false;
            }, 3000);
        },
        emptyCart: (state) => {
            state.cart = [];
            state.totalPrice = getCartTotal(state);
        },
        setGalleryVisible: (state) => {
            state.galleryVisible = true;
            state.cartListingVisible = false;
            state.checkoutFormVisible = false;
        },
        setCartListingVisible: (state) => {
            state.galleryVisible = false;
            state.cartListingVisible = true;
            state.checkoutFormVisible = false;
        },
        setCheckoutFormVisible: (state) => {
            state.galleryVisible = false;
            state.cartListingVisible = false;
            state.checkoutFormVisible = true;
        },
        updateItemQuantity: (state, { row, index, new_quantity }) => {
            let itemIdx = state.cart.findIndex(elem => elem.image.id == row.image.id);

            if (itemIdx !== undefined && itemIdx != -1) {
                if (index !== undefined && index != -1) {
                    let newItem = state.cart[itemIdx].sizes[index];
                    newItem.quantity = parseFloat(new_quantity);
                    newItem.line_price = calculateLinePrice(state, newItem);
                    state.cart[itemIdx].sizes.splice(index, 1, newItem);
                }
                state.cart[itemIdx].items_price = calculateItemsPrice(state.cart[itemIdx]);
            }
            // recalculate cart cost
            state.totalPrice = getCartTotal(state);
        },
        updateItemSize: (state, { row, index, newSize }) => {

            let itemIdx = state.cart.findIndex(elem => elem.image.id == row.image.id);
            if (itemIdx !== undefined && itemIdx != -1) {

                if (index !== undefined && index != -1) {
                    let newItem = state.cart[itemIdx].sizes[index];
                    newItem.image_size_id = newSize;
                    newItem.line_price = calculateLinePrice(state, newItem);
                    state.cart[itemIdx].sizes.splice(index, 1, newItem);
                }

                state.cart[itemIdx].items_price = calculateItemsPrice(state.cart[itemIdx]);
            }
            // recalculate cart cost
            state.totalPrice = getCartTotal(state);
        },
        hideAddedToCart: (state) => {
            clearTimeout(state._timeout);
            state.imageAddedToCart = false;
        },
        setImageSizes: (state, imageSizes) => {
            state.imageSizes = imageSizes;
        },
        clearAddedToCart: (state) => {
            state.imageAddedToCart = false;
            clearTimeout(state._timeout);
        },
        addSizeToRow: (state, row) => {
            let itemIdx = state.cart.findIndex(elem => elem.image.id == row.image.id);

            if (itemIdx !== undefined && itemIdx != -1) {
                state.cart[itemIdx].sizes.push(
                    {
                        quantity: 1,
                        image_size_id: state.imageSizes[0].id,
                        line_price: calculateLinePrice(state, { quantity: 1, image_size_id: state.imageSizes[0].id })
                    }
                );
            }
            state.cart[itemIdx].items_price = calculateItemsPrice(state.cart[itemIdx]);
            state.totalPrice = getCartTotal(state);
        },
        removeSizeRow: (state, { image_id, index }) => {
            let itemIdx = state.cart.findIndex(elem => elem.image.id == image_id);

            if (itemIdx !== undefined && itemIdx !== -1) {

                if (index  !== undefined && index !== -1) {
                    state.cart[itemIdx].sizes.splice(index, 1);
                    state.cart[itemIdx].items_price = calculateItemsPrice(state.cart[itemIdx]);
                    state.totalPrice = getCartTotal(state);
                }
            }
        },
        removeImage: (state, index) => {
            if (index !== undefined && index !== -1) {
                state.cart.splice(index, 1)
            }
            state.totalPrice = getCartTotal(state);
        },
        updateOrder: (state, order) => {
            state.order = order;
        },
        setHideCheckoutForm: (state) => {
            state.checkoutFormVisible = false;
        },
        setOrderCompleteVisible: (state, status) => {
            state.orderCompletedVisible = status;
            state.checkoutFormVisible = false;
        },
        resetOrder: (state) => {
            state.order = {
                client_name: '',
                ship_address_1: '',
                ship_address_2: '',
                ship_address_3: '',
                zip_code: '',
                locality: '',
                city: '',
                ship_same_address: true,
                total: 0.0,
                payment_method_id: null,
                phone: '',
                email: '',
            }
        }
    },
    getters: {
        getCart: state => {
            return state.cart;
        },
        getNumItems: state => {
            return state.cart.length;
        },
        getTotalPrice: state => {
            return state.totalPrice;
        },
        getGalleryVisible: state => {
            return state.galleryVisible;
        },
        getCartListingVisible: state => {
            return state.cartListingVisible;
        },
        getCheckoutFormVisible: state => {
            return state.checkoutFormVisible;
        },
        orderCompletedVisible: state => {
            return state.orderCompletedVisible;
        },
        imageAddedToCart: state => {
            return state.imageAddedToCart || false;
        },
        cartTotalItems: state => {
            return state.totalPrice;
        },
        imageSizes: (state) => {
            if (state.imageSizes.length == 0) {
                const imageSizeAPI = new ImageSizeAPI();
                return imageSizeAPI.all().then(res => {
                    return state.imageSizes;
                }).catch(err => {
                    console.log(err);
                });
            } else {
                return state.imageSizes;
            }
        },

        getOrder: state => {
            return state.order;
        },


    },
    actions: {
        showGallery: (context) => {
            context.commit('setGalleryVisible');
        },
        showCartListing: (context) => {
            context.commit('setCartListingVisible');

        },
        showCheckoutForm: (context) => {
            context.commit('setCheckoutFormVisible');
        },
        addToCart: async (context, image) => {
            context.commit('addToCart', image)
        },
        clearAddedToCart: (context) => {
            context.commit('clearAddedToCart');
        },
        emptyCart: (context) => {
            context.commit('emptyCart');
        },
        updateItemQuantity: (context, obj) => {
            context.commit('updateItemQuantity', obj);
        },
        updateItemSize: (context, obj) => {
            context.commit('updateItemSize', obj);
        },
        removeFromCart: (context, file) => {
            context.commit('updateItemQuantity', file);
        },
        hideAddedToCart: (context) => {
            context.commit('hideAddedToCart');
        },
        setImageSizes: (context, imageSizes) => {
            context.commit('setImageSizes', imageSizes);
        },
        addSizeToRow: (context, row) => {
            context.commit('addSizeToRow', row);
        },
        removeSizeRow: (context, obj) => {
            context.commit('removeSizeRow', obj);
        },
        removeImage: (context, index) => {
            context.commit('removeImage', index);
        },
        updateOrder: (context, order) => {
            context.commit('updateOrder', order);
        },
        hideCheckoutForm: (context) => {
            context.commit('setHideCheckoutForm');
        },
        orderCompletedVisible: (context) => {
            context.commit('setOrderCompletedVisible');
        },
        setOrderCompleteVisible: (context, status) => {
            context.commit('setOrderCompleteVisible', status)
        },
        resetOrder: (context) => {
            context.commit('resetOrder');
        }
    }

});
